import { z } from 'zod';

import { REFERENCE_KINDS } from '../review/schema';

/**
 * The staleness half of `backend/app/api/lists.py`'s `ReferenceCandidatePage` - which Excel
 * capture the official customer/product lists came from, and whether it is old enough that its
 * *silence* cannot be trusted (`backend/app/reporting/references.py`: "staleness is reported,
 * never enforced"). `capture` is `null` before the Windows agent has ever published this list,
 * which the note list treats the same as stale: every name is unknown until then.
 */
export const referenceCaptureSchema = z.object({
  snapshot_id: z.string(),
  source_table: z.string(),
  captured_at: z.string(),
  content_hash: z.string(),
  value_count: z.number(),
  is_stale: z.boolean(),
});

export type ReferenceCapture = z.infer<typeof referenceCaptureSchema>;

/** Only the `capture` field is read here; `items`/`next_cursor`/`kind` are ignored by `zod`'s
 * default unknown-key stripping, so `limit=1` is enough to ask for. */
export const referenceCapturePageSchema = z.object({
  capture: referenceCaptureSchema.nullable(),
});

/** One official value the owner may resolve a customer/product field to
 * (`ReferenceCandidateResponse`). `aliases` are confirmed surface forms shown for context only -
 * for the ids a retire action needs, `references/api.ts`'s `fetchAliasesForValue` is the real
 * source. */
export const referenceCandidateSchema = z.object({
  reference_value_id: z.string(),
  official_name: z.string(),
  source_row_key: z.string(),
  is_active: z.boolean(),
  aliases: z.array(z.string()),
});
export type ReferenceCandidate = z.infer<typeof referenceCandidateSchema>;

/** A full page of `GET /references/{customers|products}` - `ReferenceCandidatePage`. Unlike
 * `referenceCapturePageSchema` above (a `limit=1` peek), this is what the 6.5 reference picker
 * actually searches and lists. */
export const referenceCandidatePageSchema = z.object({
  items: z.array(referenceCandidateSchema),
  next_cursor: z.string().nullable(),
  kind: z.enum(REFERENCE_KINDS),
  capture: referenceCaptureSchema.nullable(),
});
export type ReferenceCandidatePage = z.infer<typeof referenceCandidatePageSchema>;

/** One owner-confirmed literal-reading-to-official-value mapping (`AliasResponse`). A retired
 * alias keeps `retired_at` set rather than disappearing, so the alias management surface in the
 * 6.5 picker can show history, not only the mapping currently in effect. */
export const aliasSchema = z.object({
  id: z.string(),
  kind: z.enum(REFERENCE_KINDS),
  surface_form: z.string(),
  reference_value_id: z.string(),
  created_at: z.string(),
  retired_at: z.string().nullable(),
});
export type Alias = z.infer<typeof aliasSchema>;
